import { laptopMediumMax } from '@propertypal/shared/src/constants/mediaQueries';
import styled from 'styled-components';

const laptopMediumMin = laptopMediumMax.replace('max', 'min').replace('980', '981');

export const BannerHeader = styled.div<{ premiumPage: boolean }>`
  width: 100%;
  height: 480px;
  position: relative;
  overflow: hidden;
  background-color: #000;

  @media (${laptopMediumMax}) {
    height: 300px;
  }

  ${({ premiumPage }) =>
    premiumPage
      ? `
  height: calc(100vh - 57px); // remove header height
  `
      : ``}
`;

export const BannerImage = styled.div<{ backgroundImage: string }>`
  background-image: url(${(props) => props.backgroundImage});
  background-position: center;
  background-size: cover;
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0.6;
`;

export const Banner = styled.div`
  width: 300px;
  height: 50px;
  position: absolute;
  top: 60px;
  left: -65px;
  background-color: ${(props) => props.theme.primary};
  color: ${(props) => props.theme.white};
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  padding-top: 14px;
  transform: rotate(-45deg);

  @media (${laptopMediumMax}) {
    display: none;
  }
`;

export const BannerContent = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

export const BannerTitle = styled.h3<{ premiumPage: boolean }>`
  font-size: 32px;
  font-weight: bold;
  color: ${(props) => props.theme.white};
  text-align: center;
  margin-top: 140px;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.75);

  @media (${laptopMediumMax}) {
    font-size: 22px;
    margin-top: 30px;
  }

  ${({ premiumPage }) =>
    premiumPage
      ? `
    @media screen and (max-height: 480px) and (${laptopMediumMin}) {
      margin-top: 50px;
    }
  `
      : ``}
`;

export const BannerText = styled.p`
  font-size: 26px;
  margin-top: 20px;
  text-align: center;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.75);
  color: ${(props) => props.theme.white};

  @media (${laptopMediumMax}) {
    font-size: 18px;
    margin-top: 15px;
  }
`;

export const ColumnContainer = styled.div<{ premiumPage: boolean }>`
  position: relative;
  display: flex;
  margin-top: -150px;
  justify-content: center;
  z-index: 5;

  @media (${laptopMediumMax}) {
    flex-direction: column;
    padding: 15px;
    margin-top: -165px;
  }

  ${({ premiumPage }) =>
    premiumPage
      ? `
    @media screen and (min-height: 820px) and (${laptopMediumMin}) {
      margin-top: -490px;
    }
  `
      : ``}
`;

export const Column = styled.div`
  position: relative;
  width: 440px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.2);

  &:first-child {
    margin: 0 40px 0 100px;
  }

  &:last-child {
    margin: 0 100px 0 40px;
  }

  @media (${laptopMediumMax}) {
    width: 100%;
    margin: 0 !important;
  }
`;

export const ColumnLabel = styled.div`
  background-color: ${(props) => props.theme.darkGrey};
  text-align: center;
  font-size: 16px;
  color: ${(props) => props.theme.white};
  font-weight: bold;
  padding: 4px 0 0;
  height: 30px;
`;

export const ColumnHeader = styled.div<{ color: string; roundedTop?: boolean }>`
  background-color: ${(props) => props.color};
  height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${(props) =>
    props.roundedTop &&
    `
    margin-top: 30px;
    border-radius: 6px 6px 0 0;
  `}

  h3 {
    font-size: 32px;
    color: ${(props) => props.theme.white};
    text-align: center;
    font-weight: normal;
  }

  p {
    font-size: 16px;
    color: ${(props) => props.theme.white};
    text-align: center;
    opacity: 0.6;
  }
`;

export const ColumnBody = styled.div`
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.2);
  border-radius: 0 0 8px 8px;
  flex: 1;
  background: ${({ theme }) => theme.white};
`;

export const Content = styled.div`
  position: relative;
  padding: 15px 30px;

  h3 {
    font-size: 54px;
    color: ${(props) => props.theme.textDark};
    text-align: center;
    margin-top: -15px;
  }

  h4 {
    color: ${(props) => props.theme.textDark};
    font-size: 20px;
    font-weight: 600;
    text-align: center;
    margin: 10px 0 20px;
  }

  h5 {
    font-size: 20px;
    color: ${(props) => props.theme.backgroundMid};
    text-align: center;
    font-weight: normal;
  }

  @media (${laptopMediumMax}) {
    h4 {
      font-size: 16px;
    }
  }
`;

export const ContentDivider = styled.div<{ color: string }>`
  position: relative;
  padding: 18px 0;

  h4 {
    font-size: 20px;
    color: ${(props) => props.theme.textDark};
    text-align: center;
  }

  &:before {
    content: ' ';
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: ${(props) => props.color};
    opacity: 0.1;
    top: 0;
  }
`;

export const ListHeader = styled.button<{ color: string }>`
  width: 100%;
  border-top: 1px dashed ${(props) => props.theme.keyline};
  padding: 15px 0;

  p {
    font-size: 16px;
    margin-bottom: 2px;
    color: ${(props) => props.color};
  }
`;

export const List = styled.ul`
  padding: 0 15px;

  @media (${laptopMediumMax}) {
    padding: 0;
  }
`;

export const ListRow = styled.li`
  display: flex;

  p {
    margin: -4px 0 30px 10px;
    color: ${(props) => props.theme.textDark};
  }

  &:last-child p {
    margin-bottom: 0;
  }
`;

export const FeatureBubble = styled.div`
  width: 150px;
  height: 150px;
  background-color: #fad1bf;
  transform: rotate(10deg);
  position: absolute;
  top: -50px;
  right: -75px;
  border-radius: 150px;
  padding-top: 48px;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.3);
  z-index: 3;

  h3 {
    color: ${(props) => props.theme.primary};
    font-size: 40px;
    text-align: center;
    font-weight: 800;
  }

  p {
    text-align: center;
    margin-top: -6px;
  }

  @media (${laptopMediumMax}) {
    width: 100px;
    height: 100px;
    top: -30px;
    right: -6px;
    padding: 36px 20px 0;

    h3 {
      font-size: 24px;
    }

    p {
      font-size: 14px;
      line-height: 12px;
      margin-top: -2px;
    }
  }
`;

export const PremierBubble = styled.div`
  width: 172px;
  height: 172px;
  background-color: #ebddee;
  transform: rotate(-10deg);
  position: absolute;
  top: -79px;
  left: -86px;
  border-radius: 172px;
  padding-top: 56px;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.3);
  z-index: 3;

  h3 {
    color: ${(props) => props.theme.purple};
    font-size: 48px;
    text-align: center;
    font-weight: 800;
  }

  p {
    text-align: center;
    margin-top: -6px;
  }

  @media (${laptopMediumMax}) {
    width: 100px;
    height: 100px;
    top: -30px;
    left: -10px;
    padding: 36px 20px 0;

    h3 {
      font-size: 24px;
    }

    p {
      font-size: 14px;
      line-height: 12px;
      margin-top: -2px;
    }
  }
`;

export const AstrixText = styled.p`
  color: ${(props) => props.theme.textDark};
  text-align: center;
  font-style: italic;
  padding: 0 15px;
  font-size: 14px;
  margin: 30px 0;
`;

export const FooterText = styled.p`
  color: ${(props) => props.theme.textDark};
  text-align: center;
  font-size: 16px;
  padding: 0 15px;
`;

export const DisableOverlay = styled.div`
  background-color: rgba(255, 255, 255, 0.9);
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  padding: 0 30px;

  p {
    color: ${(props) => props.theme.textDark};
    font-weight: 600;
    text-align: center;
    font-size: 20px;
  }
`;
